import { Injectable, inject } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'

/**
 * Browser-tab title in the admin's language: `<breadcrumb> - <suffix>`, built
 * from the deepest route's `data.breadcrumb` i18n key (every page route has
 * one). Routes' static English `title` stays as the fallback until the
 * translations load. Re-applied on language switch.
 */
@Injectable({ providedIn: 'root' })
export class TranslatedTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title)
  private readonly translate = inject(TranslateService)
  private last: RouterStateSnapshot | null = null

  constructor() {
    super()
    this.translate.onLangChange.subscribe(() => {
      if (this.last) this.updateTitle(this.last)
    })
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.last = snapshot
    const fallback = this.buildTitle(snapshot)
    const key = deepestBreadcrumb(snapshot.root)
    const page = key ? this.translate.instant(key) : null
    const suffix = this.translate.instant('layout.pageTitleSuffix')
    if (typeof page === 'string' && page !== key && typeof suffix === 'string' && suffix !== 'layout.pageTitleSuffix') {
      this.title.setTitle(`${page} - ${suffix}`)
    } else if (fallback) {
      this.title.setTitle(fallback)
    }
  }
}

function deepestBreadcrumb(route: ActivatedRouteSnapshot): string | null {
  let key: string | null = null
  for (let r: ActivatedRouteSnapshot | null = route; r; r = r.firstChild) {
    if (typeof r.data?.['breadcrumb'] === 'string') key = r.data['breadcrumb']
  }
  return key
}
